/* eslint-disable */
import invariant from 'invariant';
import { v4 as uuidv4 } from 'uuid';
import findKey from 'lodash/findKey';

import appActions from 'common/support/applicationActions';
import log from 'common/support/Log';
import { isGlobalObject, dumpObject } from 'common/support/objectHelper';
import { getPrefixedValue } from 'common/support/stringHelper';
import safeLocalStorage from 'common/support/safeLocalStorage';
import db8Storage from 'common/support/db8Storage';
import config from 'config';

const { userAgent } = window.navigator;

// For testing recognition of various platforms:
// userAgent = "Opera/9.80 (Linux armv71) Presto/2.12.407 Version/12.51 , M55-C2-UHD/1.3.15B-UHD (Vizio, M55-C2, Wired)";
// userAgent = "Mozilla/5.0 (Linux armv7i;U;en) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/39.0.2717.71 Safari/537.36 SRAF/3.5";
// userAgent = "Mozilla/5.0 (Linux armv7i;U;en) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/42.0.2311.152 Safari/537.36 Tbrowser/1.0, Digital-TV/V8-T56FT05-LF1V000 (TCL,32D2930,wireless)";
// userAgent = "Opera/9.80 (Linux armv7i;NETRANGEMMH;HbbTV/1.1.1;CE-HTML/1.0;Vendor/TCL;SW-Version/V8-T56FT05-LF1V005;Cnt/GBR;Lan/eng) Presto/2.12.407 Version/12.51 AppleWebKit/534.24 (KHTML, like Gecko) Chrome/11.0.696.34 Safari/534.24 Tbrowser/1.0";
// userAgent = "opera/9.80, M350/0.1.83_debug_FoxPlay_v2 (Sagemcom_Broadband_SAS,DIW350_TP,Wired) ";
// userAgent = "chromium/39, Totalplay_STB_BCM7250B0/7.4.01-31 (Netgem, N7800, Wired)";
// userAgent = "Opera/9.80 (Linux mips) Presto/2.12.407 Version/12.51 , TiVo-Device/b-tcdclient-harmony/2014.01.30-0440 (TiVo, TiVo_TCD8F6000, wired)";
// userAgent = "Mozilla/5.0 (Linux armv7l) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/42.0.2311.152 Safari/537.36 OPR/29.0.1803.0 OMI/4.5.23.0.JFK.56, TiVo_STB_BCM7449SVMS3/b-tcdclient-harmony2015.08.30-1840 (CableCo, TCD849000, wired)";
// userAgent = "Opera/9.80 (Linux armv71) Presto/2.12.407 Version/12.50 SonyCEBrowser/1.0 (DTV14/KDL-32W700B; CTV/PKG2.704AAA; CAN; en;)";
// userAgent = "Mozilla/5.0 (Linux armv71) AplleWebKit/537.36 (KHTML, like Gecko) Chrome35.0.1916.153 OMI/4.2.12.48.ALSAN3.56 TV Store/4.2 Model/Sony-KDL-SE2N";

// 2017 Vizio Sigma
// userAgent = "Mozilla/5.0 (Linux armv7l) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/53.0.2785.89 Safari/537.36 OPR/40.0.2207.0 OMI/4.9.0.X.release,  _TV_D50-E1-UHD_2017/Build_298_2016-10-20_03-06-26  (Vizio, D50-E1, Wired)";

// Pre 2016 Sigma?
// userAgent ="Opera/9.80 (Linux armv7l) Presto/2.12.407 Version/12.51 , P502ui-B1-UHD/V1.5.8-UHD (Vizio, P502ui-B1, Wired)";

// 2017 Vizio MTK:
// userAgent = "Opera/9.80 (Linux armv7I) Presto/2.12.362 Version/12.11";
// userAgent = "Mozilla/5.0 (Linux armv7l) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/49.0.2623.112 Safari/537.36 0.129.ALISHAN6 VIZIO-DTV/V7.0.28 (Vizio, D32f-E1, Wired)";
// userAgent = "Mozilla/5.0 (Linux armv7l) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/53.0.2785.143 Safari/537.36 OPR/40.0.2207.0 OMI/4.9.0.73.Clio.10 , _TV_D43-E2-UHD/17D-0.8.8.3-UHD (Vizio, D43-E2, Wireless) Model/SigmaDesigns-SX7";

// 2017 Vizio E50X-E1:
// userAgent = "Mozilla/5.0 (X11; Linux armv7l) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/52.0.2743.84 Safari/537.36 CrKey/1.22.78594";

// Sony Blue Ray:
// userAgent = "Mozilla/5.0 (Linux armv7l) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/42.0.2311.152 Safari/537.36 OPR/29.0.1803.0 OMI/4.5.23.37.ALSAN5.57 Model/Sony-BDP-S3700 SonyCEBrowser/1.0 (BDP-S3700; M30.D.0380; USA;";

let manufacturer = 'unknown';
const unknownModel = 'unknown';
let model: string | undefined = unknownModel;
let isVizioOldMtk = false;
let isSonyBlueRay = false;
let version: string | undefined | null = '0.0.0';
let detail: any;
let deviceId = uuidv4();
let deviceNetwork = 'unknown';
const userAgentPattern = userAgent.toLowerCase();
let isUniversalWindows = false;
let windowsBuildVersionInfo: Record<string, any> = {};
let rdid: string | number = 'unknown';
let isLAT: boolean = !!0;
let usePPID = false;
let connectionStatus: boolean | null = null;
let getConnectionStatus = (): Promise<boolean | null> => Promise.resolve(connectionStatus);

// We need to track specific Tizen models and versions to work around some platform issues.
let tizenOsVersion = 0;
let tizenModelYear = 0;

let isSlowDevice = false;
let isFullyPerformant = false; // i.e. fast with good GC
let isMemoryConstrained = false;
let usesOpera = false;
const usesSafari =
  userAgent.indexOf('Safari') >= 0 && userAgent.indexOf('Chrome') < 0;
const usesEdge = userAgent.indexOf('Edge') >= 0;

let isLocalStorageSupported = !!window.localStorage;

// Some vendors have multiple model series with differing storage abilities. It's best to write to both for reliability.
let useBothLocalAndCookieStorage = false;

let usesCORS = true; // assume security needed by default

let useWindowScroll = true;
let useScrollTop = false;
let areScrollBarsVisible = false;

let useCssTransitions = true;
let useCssAnimation = true;
let useSvgAnimation = false;
let supportsSvg = true;
let supportsImageMasks = true; // Determines if we are using css mask-image
let isVizioSmartCastDevice = false;

let useVirtualKeyboard = false;

let useSystemCCSettings = false;
let supportsNativeCC = false;
let useCCAppAction = true;

const PLATFORM_UNKNOWN = 'unknown';
const PLATFORM_SAMSUNG_SMART_TV = 'samsung-orsay';
const PLATFORM_SAMSUNG_TIZEN = 'samsung-tizen';
const PLATFORM_VIZIO = 'vizio';
const PLATFORM_TCL = 'tcl';
const PLATFORM_LG_WEB_OS = 'lg-webos';
const PLATFORM_LG_NETCAST = 'lg-netcast';
const PLATFORM_SONY_HTML5 = 'sony';
const PLATFORM_SAGEM = 'sagem';
const PLATFORM_NETGEM = 'netgem';
const PLATFORM_TIVO = 'tivo';
const PLATFORM_PS3 = 'ps3';
const PLATFORM_PS4 = 'ps4';
const PLATFORM_XBOXONE = 'xboxone';
const PLATFORM_WINDOWS_DESKTOP = 'windows-desktop';
const PLATFORM_ANDROIDTV = 'androidtv';
const PLATFORM_FIRETV = 'firetv';
const PLATFORM_VEGA = 'vega';

const DEVICE_ID_KEY = 'deviceId';
const MVPD_DEVICE_ID_KEY = 'mvpdDeviceId';

let platform = PLATFORM_UNKNOWN;
let platformVariant: string | undefined;

// Standard ASCII key codes
const keyCodes = {
  space: 32,
  enter: 13,
  esc: 27,
  del: 127,
  backspace: 8,
  zero: 48,
  one: 49,
  two: 50,
  three: 51,
  four: 52,
  five: 53,
  six: 54,
  seven: 55,
  eight: 56,
  nine: 57,
  A: 65,
  S: 83,
  D: 68,
  F: 70,
  L: 76,
  M: 77,
  O: 79,
  P: 80,
  Q: 81,
  W: 87,
  X: 88,
  Z: 90,

  leftArrow: 37,
  rightArrow: 39,
  upArrow: 38,
  downArrow: 40,

  deleteAll: 46,
};
type ShowVirtualKeyboard = ((inputElementId?: string, inputTitle?: string, inputDescription?: string, maxLength?: number | string) => Promise<string | undefined>)
  | (((inputElementId: string, getFinalInputText?: () => Promise<string>) => Promise<string | undefined>) | undefined);
let actionKeyMap: Record<string, number | number[]> = {};

let hasKeyboard = false;
let hasNumericKeys = true;
let keyboardUsesInputField = false;
let preventInputFocus = false;
let showVirtualKeyboard: ShowVirtualKeyboard;
let isShowingVirtualKeyboard = false;
let supportsTouch = false;

let useDRM = usesEdge; // enable playready if on Edge browser
let useEME = usesEdge; // e.g. the Encrypted Media Extension for HTML5 video element

let supportsSmoothStreaming = usesEdge; // enable SS if on Edge browser
let supportsHLS = usesSafari || usesEdge;
let supportsPlayreadyDASH = false;
let supportsWidevineDASH = false;
let supportsInitialVideoSeek = true;
let doInitialSeekOnlyAfterVideoStart = true; // ensure the video is loaded/started by default before seeking
let pauseForInitialVideoSeek = false; // to prevent frame 0 from showing on some devices like Vizio M43 Sigma models
let seekIsReliable = true; // if false we have to do work arounds to ensure that REW/FF works well
let allowPlaybackWithoutDuration = false; // true if we can't rely on duration being known before playback, e.g. LG Netcast
let supportsAutoSuspendResumePlayback = false;

let supportedFeatures = {
  supports4K: false,
  supportsHDR10: false,
  supportsDolbyVision: false,
  supportsDolbyAtmos: false,
  supportsDolbyDigital: false,
};

let getMpxTokenMap = (media: { pid: string }): Record<string, string> => {
  return { '{media-id}': media.pid };
};

let exitApp = () => {
  try {
    window.close();
  } catch (err) {
    // no-op
  }
  try {
    // @ts-ignore
    window.location = 'about:blank';
  } catch (err) {
    // no-op
  }
};

// Like exitApp but via the return key of the remote.
// On some platforms this leads one to the original screen
// used to launch the app.
let returnFromApp = (): void => {
  exitApp(); // by default we just do the exit action.
};

let getDetail = (): Promise<any> => Promise.resolve(detail);

let getDeviceId = (): Promise<string> => defaultGetDeviceId;
const defaultGetDeviceId: Promise<string> = new Promise(resolve => {
  const storedId = safeLocalStorage.getItem(DEVICE_ID_KEY);
  if (storedId) {
    resolve(storedId);
    return;
  }

  safeLocalStorage.setItem(DEVICE_ID_KEY, deviceId);
  resolve(deviceId);
});

let getDeviceNetworkConnection = (): Promise<boolean | string> =>
  new Promise((resolve, reject) => {
    if (window && window.navigator) {
      const networkStatus = window.navigator.onLine;
      resolve(networkStatus);
    } else {
      reject();
    }
  });

let getRDID = (): Promise<string | number> => Promise.resolve(rdid);
let getLAT = (): Promise<boolean> => Promise.resolve(isLAT);

let advertiserIdListener: ((rdid: string | number, isLAT: boolean) => void) | null = null;
let setAdvertiserIDListener = (callback: ((rdid: string | number, isLAT: boolean) => void) | null): void => {
  advertiserIdListener = callback;
};

let blockKeyEventNavigation = (_event?: Event): void => {
  // Override per platform
};

const stopKeyEventPropagation = (event: Event): void => {
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();
  blockKeyEventNavigation(event);
};

let hasSpeechSynthesisCapability = (): boolean => !!window.speechSynthesis;

let hasVegaBridge = (): boolean => false;

let requiresUserGestureForSpeech = true;

// Override per platform
let accessibleTextDisplayEnabled = (): boolean => false;

// Override per platform. Async because some platforms (e.g. LG webOS) read this
// from an asynchronous system-settings API.
let accessibleAudioEnabled = (): Promise<boolean> => Promise.resolve(false);

let getIpAddress = (): any => {};

let setScreenSaver = (_on?: boolean): void => {};

let playerRef: any = null;
let setPlayerRef = (_player?: any): void => {};

let speechSynthesisWatchdog: ReturnType<typeof setInterval> | null = null;

const ensureSpeechSynthesisWatchdog = (): void => {
  if (speechSynthesisWatchdog || !window.speechSynthesis) {
    return;
  }
  speechSynthesisWatchdog = setInterval(() => {
    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.pause();
      window.speechSynthesis.resume();
    }
  }, 5000);
};

const startTTSSpeechSynthesis = (text: string): void => {
  ensureSpeechSynthesisWatchdog();
  if (window.speechSynthesis.speaking || window.speechSynthesis.pending) {
    window.speechSynthesis.cancel();
  }
  const utterance = new SpeechSynthesisUtterance(text);
  window.speechSynthesis.speak(utterance);
};

const stopTTSSpeechSynthesis = (): void => {
  window.speechSynthesis.cancel();
};

let useAriaAccessibility = false;

let firstHiddenAriaElement: HTMLElement | null;
let secondHiddenAriaElement: HTMLElement | null;
let usingAriaFocus1 = false;

const startTTSAria = (text: string): void => {
  const ariaText = text ? text : '';
  const element = nextAriaHiddenElement();
  element && element.setAttribute('aria-label', ariaText);
  element && element.focus();
};

const stopTTSAria = (): void => {
  const element = document.getElementById('aria-hidden-stop');
  element?.focus();
};

let startTTS = (text: string): void => {
  // Override per platform if doesn't support either speech synthesis or aria accessibility
  if (hasSpeechSynthesisCapability() && window.speechSynthesis) {
    startTTSSpeechSynthesis(text);
  } else if (useAriaAccessibility) {
    startTTSAria(text);
  }
};
let stopTTS = (): void => {
  // Override per platform if doesn't support either speech synthesis or aria accessibility
  if (hasSpeechSynthesisCapability() && window.speechSynthesis) {
    stopTTSSpeechSynthesis();
  } else if (useAriaAccessibility) {
    stopTTSAria();
  }
};

const useTtsFocus = false;

let subscribeToAccessibilityChanges = (_callback?: (enabled: boolean) => void): void => {
  // Override per platform
};

// For saving errors until the logger is established for remote recording.
const startupErrors: string[] = [];

function reportStartupErrors(): boolean {
  if (startupErrors.length > 0) {
    startupErrors.forEach(msg => {
      log.error(msg);
    });
    return true;
  }
  return false;
}

// Create a minimally usable device configuration for load time queries.
const thisDevice = createDeviceDescription();

// Accurate platform usage will require an initialize call first.
thisDevice.initializePlatform = (platformName: string): void => {
  try {
    platform = detectPlatform(platformName);
    switch (platform) {
      case PLATFORM_SAMSUNG_SMART_TV:
        actionKeyMap = configureForSamsungSmartTV();
        break;
      case PLATFORM_SAMSUNG_TIZEN:
        actionKeyMap = configureForSamsungTizen();
        break;
      case PLATFORM_VIZIO:
        actionKeyMap = configureForVizio();
        break;
      case PLATFORM_VEGA:
        actionKeyMap = configureForVega();
        break;
      case PLATFORM_TCL:
        actionKeyMap = configureForTcl();
        break;
      case PLATFORM_LG_WEB_OS:
        actionKeyMap = configureForLgWebOs();
        break;
      case PLATFORM_LG_NETCAST:
        actionKeyMap = configureForLgNetCast();
        break;
      case PLATFORM_SONY_HTML5:
        actionKeyMap = configureForSony();
        break;
      case PLATFORM_SAGEM:
        actionKeyMap = configureForSagem();
        break;
      case PLATFORM_NETGEM:
        actionKeyMap = configureForNetgem();
        break;
      case PLATFORM_TIVO:
        actionKeyMap = configureForTiVo();
        break;
      case PLATFORM_PS3:
        actionKeyMap = configureForPS3();
        break;
      case PLATFORM_PS4:
        actionKeyMap = configureForPS4();
        break;
      case PLATFORM_XBOXONE:
        actionKeyMap = configureForXboxOne();
        break;
      case PLATFORM_WINDOWS_DESKTOP:
        actionKeyMap = configureForUniversalWindows();
        break;
      case PLATFORM_ANDROIDTV:
        actionKeyMap = configureForAndroidTV();
        break;
      case PLATFORM_FIRETV:
        actionKeyMap = configureForFireTV();
        break;
      case PLATFORM_UNKNOWN:
      default:
        actionKeyMap = configureForUnknownPlatform();
        break;
    }
  } catch (err) {
    startupErrors.push(`platform error: ${log.describeError(err)}`);
  }

  // Update the existing exported device values.
  const updatedDevice = createDeviceDescription();
  Object.keys(updatedDevice).forEach(key => {
    thisDevice[key] = updatedDevice[key];
  });
};

export default thisDevice;

/**
 * Presents the characteristics of the current browser/OS
 */
function createDeviceDescription(): Record<string, any> {
  if (!platformVariant || platformVariant === PLATFORM_UNKNOWN)
    platformVariant = platform;

  return {
    reportStartupErrors,
    userAgent,

    isWindows: userAgent.indexOf('Windows') >= 0,
    isMac: userAgent.indexOf('Macintosh') >= 0,

    isFirefox: userAgent.indexOf('Firefox') >= 0,

    platform,
    platformVariant,
    manufacturer,
    model,
    version,
    detail,
    getDetail,
    getDeviceId,
    getDeviceNetworkConnection,
    startTTS,
    stopTTS,
    isLocalStorageSupported,
    useBothLocalAndCookieStorage,
    usesCORS,

    useWindowScroll,
    useScrollTop,
    areScrollBarsVisible,

    useCssTransitions,
    useCssAnimation,
    useSvgAnimation,
    supportsSvg,

    supportsImageMasks,

    isSlowDevice, // avoid expensive processing when true
    isFullyPerformant, // i.e. fast with good GC
    isMemoryConstrained, // avoid excessive memory usage and caching when true
    usesOpera,

    isUnknown: platform === PLATFORM_UNKNOWN,
    isSamsungSmartTV: platform === PLATFORM_SAMSUNG_SMART_TV,

    isSamsungTizen: platform === PLATFORM_SAMSUNG_TIZEN,
    tizenModelYear,
    tizenOsVersion,

    isVizio: platform === PLATFORM_VIZIO,
    isVega: platform === PLATFORM_VEGA,
    isVizioOldMtk,
    isLG_WebOS: platform === PLATFORM_LG_WEB_OS,
    isLG_NetCast: platform === PLATFORM_LG_NETCAST,
    isLG: platform === PLATFORM_LG_WEB_OS || platform === PLATFORM_LG_NETCAST,
    isTcl: platform === PLATFORM_TCL,
    isSonyHTMl5: platform === PLATFORM_SONY_HTML5,
    isSonyBlueRay,
    isSagem: platform === PLATFORM_SAGEM,
    isNetgem: platform === PLATFORM_NETGEM,
    isTivo: platform === PLATFORM_TIVO,
    isPS3: platform === PLATFORM_PS3,
    isPS4: platform === PLATFORM_PS4,
    isPlayStation: platform === PLATFORM_PS3 || platform === PLATFORM_PS4,
    isXboxOne: platform === PLATFORM_XBOXONE,
    isVizioSmartCastDevice,
    isWindowsDesktop: platform === PLATFORM_WINDOWS_DESKTOP,
    isUniversalWindows,
    isAndroidTV: platform === PLATFORM_ANDROIDTV,
    isFireTV: platform === PLATFORM_FIRETV,
    isAndroidOrFireTV:
      platform === PLATFORM_ANDROIDTV || platform === PLATFORM_FIRETV,

    windowsBuildVersionInfo,

    useDRM,
    useEME,
    supportsHLS,
    supportsPlayreadyDASH,
    supportsWidevineDASH,
    supportsSmoothStreaming,
    supportsInitialVideoSeek,
    doInitialSeekOnlyAfterVideoStart,
    pauseForInitialVideoSeek,
    seekIsReliable,
    allowPlaybackWithoutDuration,
    supportsAutoSuspendResumePlayback,
    detectPlatform,
    supportsNativeCC,
    useCCAppAction,

    hasKeyboard,
    hasNumericKeys,
    keyCodes,
    getKeyCodeAction,
    showVirtualKeyboard,
    isShowingVirtualKeyboard() {
      return isShowingVirtualKeyboard;
    },
    keyboardUsesInputField,
    preventInputFocus,
    supportsTouch,

    supportedFeatures,

    getMpxTokenMap,

    stopKeyEventPropagation,
    blockKeyEventNavigation,

    hasSpeechSynthesisCapability,
    hasVegaBridge,
    requiresUserGestureForSpeech,
    accessibleTextDisplayEnabled,
    accessibleAudioEnabled,
    useAriaAccessibility,
    useTtsFocus,
    subscribeToAccessibilityChanges,

    exitApp,
    returnFromApp,

    useVirtualKeyboard,
    useSystemCCSettings,

    rdid,
    getRDID,
    getLAT,
    setAdvertiserIDListener,
    usePPID,

    getIpAddress,

    setScreenSaver,
    setPlayerRef,

    connectionStatus,
    getConnectionStatus,
  };
}

function match(key: string): boolean {
  return userAgentPattern.indexOf(key) >= 0;
}

function detectPlatform(platformName: string): string {
  if (window.PalmSystem) {
    // TBD: LG Netcast
    return PLATFORM_LG_WEB_OS;
  }
  if (userAgent.match(/LG Browser/i)) {
    return PLATFORM_LG_NETCAST;
  }
  if (isGlobalObject('Common', 'API', 'Widget')) {
    return PLATFORM_SAMSUNG_SMART_TV;
  }
  if (window.tizen) {
    return PLATFORM_SAMSUNG_TIZEN;
  }
  if (userAgent.indexOf('Sagem') >= 0) {
    return PLATFORM_SAGEM;
  }
  if (userAgent.indexOf('Netgem') >= 0) {
    return PLATFORM_NETGEM;
  }
  if (platformName === PLATFORM_TCL && userAgent.indexOf('Tbrowser/') >= 0) {
    // E.g. TCL MT5655 model.
    return PLATFORM_TCL;
  }
  if (platformName === PLATFORM_TCL && userAgent.indexOf('SRAF/') >= 0) {
    // E.g. TCL MS6488 model.
    return PLATFORM_TCL;
  }
  if (userAgent.indexOf('Opera') >= 0 && userAgent.indexOf('Presto') >= 0) {
    usesOpera = true;

    // Seems to work now that we are using -o- prefixes better
    // useCssTransitions = false; // assume false for Presto unless we know otherwise
    // useCssAnimation = false;

    if (userAgent.indexOf('SonyCEBrowser') >= 0) return PLATFORM_SONY_HTML5;
    if (platformName === PLATFORM_TCL) return PLATFORM_TCL;
    if (platformName === PLATFORM_VIZIO) return PLATFORM_VIZIO;
    if (platformName === PLATFORM_VEGA) return PLATFORM_VEGA;
    if (userAgent.indexOf('TiVo') >= 0) return PLATFORM_TIVO;
  } else if (userAgent.indexOf('Sony') >= 0) {
    return PLATFORM_SONY_HTML5;
  } else if (userAgent.indexOf('Vizio') >= 0) {
    return PLATFORM_VIZIO;
  } else if (platformName === PLATFORM_VEGA) {
    return PLATFORM_VEGA;
  } else if (
    (platformName === PLATFORM_VIZIO && userAgent.indexOf('CrKey') >= 0) ||
    userAgent.indexOf('VIZIO SmartCast') >= 0
  ) {
    // This is a very specific case in where we are using a smart cast supported Vizio TV
    isVizioSmartCastDevice = true;
    return PLATFORM_VIZIO;
  } else if (userAgent.indexOf('TiVo') >= 0) {
    return PLATFORM_TIVO;
  } else if (userAgent.indexOf('PlayStation 3') >= 0) {
    return PLATFORM_PS3;
  } else if (userAgent.indexOf('PlayStation 4') >= 0) {
    return PLATFORM_PS4;
  } else if (
    typeof Windows !== 'undefined' &&
    typeof Windows.System !== 'undefined' &&
    typeof Windows.System.Profile !== 'undefined' &&
    typeof Windows.System.Profile.AnalyticsInfo !== 'undefined'
  ) {
    const { deviceFamily } = Windows.System.Profile.AnalyticsInfo.versionInfo;

    // Can support different device families: Phone, Desktop, Xbox, Iot, Continuum
    if (deviceFamily === 'Windows.Xbox') return PLATFORM_XBOXONE;
    return PLATFORM_WINDOWS_DESKTOP;
  } else if (
    (platformName === PLATFORM_ANDROIDTV || platformName === PLATFORM_FIRETV) &&
    userAgent.indexOf('Android') >= 0
  ) {
    if (userAgent.indexOf('AFT') >= 0) {
      return PLATFORM_FIRETV;
    }
    return PLATFORM_ANDROIDTV;
  }

  return PLATFORM_UNKNOWN;
}

function getDefaultKeyMap(): Record<string, number | number[]> {
  const keyMap: Record<string, number | number[]> = {};
  keyMap[appActions.moveLeft] = keyCodes.leftArrow;
  keyMap[appActions.moveRight] = keyCodes.rightArrow;
  keyMap[appActions.moveUp] = keyCodes.upArrow;
  keyMap[appActions.moveDown] = keyCodes.downArrow;
  keyMap[appActions.select] = keyCodes.enter;
  keyMap[appActions.back] = keyCodes.esc;
  keyMap[appActions.delete] = [keyCodes.backspace, keyCodes.del];
  keyMap[appActions.num0] = keyCodes.zero;
  keyMap[appActions.num1] = keyCodes.one;
  keyMap[appActions.num2] = keyCodes.two;
  keyMap[appActions.num3] = keyCodes.three;
  keyMap[appActions.num4] = keyCodes.four;
  keyMap[appActions.num5] = keyCodes.five;
  keyMap[appActions.num6] = keyCodes.six;
  keyMap[appActions.num7] = keyCodes.seven;
  keyMap[appActions.num8] = keyCodes.eight;
  keyMap[appActions.num9] = keyCodes.nine;

  return keyMap;
}

function addTestingKeyCodes(keyMap: Record<string, number | number[]>): void {
  // For testing with a physical keyboard.
  keyMap[appActions.playPause] = keyCodes.D;
  keyMap[appActions.fastForward] = keyCodes.F;
  keyMap[appActions.rewind] = keyCodes.S;

  // For testing left/right shoulder actions.
  keyMap[appActions.leftShoulder1] = keyCodes.Q;
  keyMap[appActions.leftShoulder2] = keyCodes.A;
  keyMap[appActions.rightShoulder1] = keyCodes.P;
  keyMap[appActions.rightShoulder2] = keyCodes.L;
  keyMap[appActions.menu] = keyCodes.M;
  keyMap[appActions.exit] = keyCodes.X;
}

function getOperaTVKeyMap(): Record<string, number | number[]> {
  const keyMap = getDefaultKeyMap();
  keyMap[appActions.play] = 415;
  keyMap[appActions.pause] = 19;
  keyMap[appActions.fastForward] = 417;
  keyMap[appActions.rewind] = 412;
  keyMap[appActions.stop] = 413;
  keyMap[appActions.prevTrack] = 424;
  keyMap[appActions.nextTrack] = 425;
  return keyMap;
}

function getLGKeyMap(): Record<string, number | number[]> {
  const keyMap = getDefaultKeyMap();
  keyMap[appActions.back] = 461;

  keyMap[appActions.play] = 415;
  keyMap[appActions.pause] = 19;
  keyMap[appActions.fastForward] = 417;
  keyMap[appActions.rewind] = 412;
  keyMap[appActions.stop] = 413;

  keyMap[appActions.red] = 403;
  keyMap[appActions.green] = 404;
  keyMap[appActions.yellow] = 405;
  keyMap[appActions.blue] = 406;
  keyMap[appActions.captions] = 460;
  return keyMap;
}

function getPlaystationKeyMap(): Record<string, number | number[]> {
  const keyMap = getDefaultKeyMap();
  keyMap[appActions.buttonSquare] = 32;
  keyMap[appActions.buttonTriangle] = 112;
  keyMap[appActions.buttonX] = 13;
  keyMap[appActions.buttonCircle] = 8;

  keyMap[appActions.leftShoulder1] = 116; // L1
  keyMap[appActions.rightShoulder1] = 117; // R1

  keyMap[appActions.leftShoulder2] = 118; // L2
  keyMap[appActions.rightShoulder2] = 119; // R2

  keyMap[appActions.leftStick] = 120; // L3
  keyMap[appActions.rightStick] = 121; // R3

  keyMap[appActions.delete] = keyCodes.del;

  // BD Remote & CEC only
  keyMap[appActions.back] = [keyCodes.esc, keyCodes.backspace];
  keyMap[appActions.menu] = 36;
  keyMap[appActions.green] = 133; // used for test automation by stb-tester
  keyMap[appActions.red] = 132;
  keyMap[appActions.yellow] = 41;
  keyMap[appActions.blue] = 42;
  keyMap[appActions.prevTrack] = 122;
  keyMap[appActions.nextTrack] = 123;
  keyMap[appActions.skipForward] = 124;
  keyMap[appActions.skipBackward] = 125;
  keyMap[appActions.fastForward] = 126;
  keyMap[appActions.rewind] = 127; // del on keyboard, rewind key on remote
  keyMap[appActions.play] = 128;
  keyMap[appActions.pause] = 130;
  keyMap[appActions.stop] = 129; // stop button acts the same as the pause button on a ps4 tv remote
  // keyMap[appActions.info] = 44;
  return keyMap;
}

function getUniversalWindowsKeyMap(): Record<string, number | number[]> {
  const keyMap = getDefaultKeyMap();
  addTestingKeyCodes(keyMap);
  keyMap[appActions.menu] = 207;

  keyMap[appActions.buttonY] = 198;
  keyMap[appActions.buttonX] = 197;

  keyMap[appActions.leftShoulder1] = 200; // LB
  keyMap[appActions.rightShoulder1] = 199; // RB

  keyMap[appActions.leftShoulder2] = 201; // LT
  keyMap[appActions.rightShoulder2] = 202; // RT

  keyMap[appActions.leftStick] = 209; // L3
  keyMap[appActions.rightStick] = 210; // R3

  // Override to support both controller and keyboard
  keyMap[appActions.moveLeft] = [keyCodes.leftArrow, 214, 205];
  keyMap[appActions.moveRight] = [keyCodes.rightArrow, 213, 206];
  keyMap[appActions.moveUp] = [keyCodes.upArrow, 211, 203];
  keyMap[appActions.moveDown] = [keyCodes.downArrow, 212, 204];
  keyMap[appActions.select] = [keyCodes.enter, 195]; // e.g. and buttonA
  keyMap[appActions.back] = [keyCodes.esc, keyCodes.del, 196]; // e.g. and buttonB

  return keyMap;
}

function getAndroidTVKeyMap(): Record<string, number | number[]> {
  const keyMap = getDefaultKeyMap();

  keyMap[appActions.playPause] = 179;
  keyMap[appActions.fastForward] = 228;
  keyMap[appActions.rewind] = 227;
  keyMap[appActions.extra] = 82;

  return keyMap;
}

function configureForUnknownPlatform(): Record<string, number | number[]> {
  isFullyPerformant = true; // assume we are on the desktop
  keyboardUsesInputField = true; // renable to test with hardware keyboard
  showVirtualKeyboard = showImplicitKeyboard;
  hasKeyboard = true;
  supportsHLS = false; // unfortunately not supported on desk tops.
  supportsNativeCC = true;
  useVirtualKeyboard = true;
  const keyMap = getDefaultKeyMap();
  addTestingKeyCodes(keyMap);

  getDetail = () =>
    Promise.resolve({
      deviceName: 'UNKNOWN',
      OSVersion: 'UNKNOWN',
      sdkVersion: 'UNKNOWN',
      screenWidth: 'UNKNOWN',
      screenHeight: 'UNKNOWN',
    });

  return keyMap;
}

function configureForSamsungSmartTV(): Record<string, number | number[]> {
  useCssTransitions = true;
  useCssAnimation = true; // works now that we are using -webkit- prefixes

  supportsHLS = true;
  supportsSmoothStreaming = true;

  usesCORS = false;

  const widgetAPI = new Common.API.Widget();
  const tvKey = new Common.API.TVKeyValue();

  hasSpeechSynthesisCapability = () =>
    window.webapis._plugin('RECOG', 'IsSupportTTS') && !!window.speechSynthesis;

  const isTtsEngineEnabled = () => {
    if (!hasSpeechSynthesisCapability()) {
      return false;
    }

    // TTS enabled (System->Accessibility->Voice guide)
    const isAccessibleAudioEnabled = window.webapis._plugin(
      'RECOG',
      'IsTTSEngineEnabled',
    );

    return !!isAccessibleAudioEnabled;
  };

  accessibleTextDisplayEnabled = () => isTtsEngineEnabled();

  accessibleAudioEnabled = () => Promise.resolve(isTtsEngineEnabled());

  seekIsReliable = false; // seems to allow seek steps of at most 5-8 seconds.

  const keyMap: Record<string, number | number[]> = {};
  keyMap[appActions.moveLeft] = tvKey.KEY_LEFT;
  keyMap[appActions.moveRight] = tvKey.KEY_RIGHT;
  keyMap[appActions.moveUp] = tvKey.KEY_UP;
  keyMap[appActions.moveDown] = tvKey.KEY_DOWN;
  keyMap[appActions.select] = [tvKey.KEY_ENTER, tvKey.KEY_PANEL_ENTER];
  keyMap[appActions.exit] = tvKey.KEY_EXIT;
  keyMap[appActions.back] = [tvKey.KEY_RETURN, tvKey.KEY_PANEL_RETURN];
  keyMap[appActions.play] = tvKey.KEY_PLAY;
  keyMap[appActions.pause] = tvKey.KEY_PAUSE;
  keyMap[appActions.fastForward] = tvKey.KEY_FF;
  keyMap[appActions.rewind] = tvKey.KEY_RW;
  keyMap[appActions.stop] = tvKey.KEY_STOP;
  keyMap[appActions.search] = tvKey.KEY_SEARCH;
  keyMap[appActions.num1] = tvKey.KEY_1;
  keyMap[appActions.num2] = tvKey.KEY_2;
  keyMap[appActions.num3] = tvKey.KEY_3;
  keyMap[appActions.num4] = tvKey.KEY_4;
  keyMap[appActions.num5] = tvKey.KEY_5;
  keyMap[appActions.num6] = tvKey.KEY_6;
  keyMap[appActions.num7] = tvKey.KEY_7;
  keyMap[appActions.num8] = tvKey.KEY_8;
  keyMap[appActions.num9] = tvKey.KEY_9;
  keyMap[appActions.num0] = tvKey.KEY_0;

  manufacturer = 'Samsung';
  let tvInfo;
  try {
    tvInfo = window.webapis.tv.info;
    model = tvInfo.getModel();
    const firmware = tvInfo.getFirmware();
    detail = `${model}/${firmware}`;
    version = firmware; // fallback in case we crash with the version below
    version = tvInfo.getVersion();
    detail += `/v${version}`;

    // We assume playready 2.5 DRM, that is supported only on 2014 models and up.
    const modelYear = parseInt(model ?? '', 10);
    useDRM = modelYear >= 14;
    // window.screenTrace("smarttv model year: " + modelYear + " useDRM: " + useDRM);
  } catch (err) {
    // no-op
  }

  exitApp = () => {
    widgetAPI.sendExitEvent();
  };

  returnFromApp = () => {
    widgetAPI.sendReturnEvent();
  };

  blockKeyEventNavigation = event => {
    widgetAPI.blockNavigation(event);
  };
  showVirtualKeyboard = ((
    inputElementId: string,
    inputTitle?: string,
    inputDescription?: string,
    maxLength?: string | number,
  ) =>
    new Promise<string | undefined>((resolve, reject) => {
      let lastTextChange: string | undefined | null;
      let ime: undefined | IMEShell_Common;
      try {
        ime = new IMEShell_Common();
        ime.inputboxID = inputElementId;

        if (
          inputDescription &&
          inputTitle &&
          inputDescription.startsWith(inputTitle)
        ) {
          // Omit title if it is a prefix anyway.
          // @ts-ignore
          inputTitle = null;
        }
        if (inputTitle) ime.inputTitle = inputTitle;
        if (inputDescription) ime.inputDescription = inputDescription;

        if (maxLength) ime.setMaxlength(typeof maxLength === 'number' ? maxLength : parseInt(maxLength, 10));
        // @ts-ignore - race condition makes this safe but when this phase is done we can fix this
        ime.onKeyPressFunc = handleKeystroke;
        // @ts-ignore - same here
        ime.onTextChangeFunc = onTextChanged;

        ime.onShow();
        isShowingVirtualKeyboard = true;
      } catch (err) {
        log.error(`ime error: ${log.describeErrorWithStack(err)}`);
        reject(err);
      }

      const onTextChanged = (newText: string) => {
        lastTextChange = newText;
      };

      const handleKeystroke = (keyCode: number) => {
        try {
          const action = getKeyCodeAction(keyCode);
          // log.debug("handleKeystroke: {0} gives {1}", keyCode, action);
          if (action === appActions.back || action === appActions.select) {
            ime?.onClose();
            // @ts-ignore
            document.activeElement.blur();
            const finalResult = action === appActions.select ? lastTextChange ?? undefined : undefined;
            isShowingVirtualKeyboard = false;
            resolve(finalResult);
            return false;
          }
        } catch (err) {
          log.error(`handleKeystroke: ${log.describeErrorWithStack(err)}`);
        }
        return true;
      };
    })) as ShowVirtualKeyboard;

  return keyMap;
}

function configureForSamsungTizen(): Record<string, number | number[]> {
  supportsHLS = true;
  supportsSmoothStreaming = true;
  useDRM = true;
  keyboardUsesInputField = true; // we need to show the input field while the keyboard is displaed.
  showVirtualKeyboard = showImplicitKeyboard;
  useSystemCCSettings = true;

  const { tizen, webapis } = window;
  const captionsKey = webapis.tvinfo.TvInfoMenuKey.CAPTION_ONOFF_KEY;
  const voiceKey = webapis.tvinfo.TvInfoMenuKey.VOICE_GUIDE_KEY;

  manufacturer = 'Samsung';

  if (webapis && webapis.adinfo) {
    try {
      const { getTIFA, isLATEnabled } = webapis?.adinfo;
      rdid = getTIFA();

      // Requires both viewing info services and
      // interest-based ad service privacy notice
      // to be unchecked to opt out (1).
      // Opt in is checked on (0).

      isLAT = !!(isLATEnabled && isLATEnabled() ? 1 : 0);
    } catch {
      // no-op
    }
  }

  if (webapis && webapis.productinfo) {
    try {
      const info = webapis.productinfo;
      const avinfo = webapis.avinfo;
      model = info.getRealModel();
      version = info.getFirmware(); // this is the actual interesting version to see.
      supportedFeatures.supports4K = info.isUdPanelSupported();
      supportedFeatures.supportsHDR10 = avinfo.isHdrTvSupport();
      supportedFeatures.supportsDolbyDigital = tizen.tvaudiocontrol
        .getOutputMode()
        .includes('DOLBY');

      const getDeviceIdPromise = new Promise<string>(resolve => {
        resolve(info.getDuid());
      });

      getDeviceId = () => getDeviceIdPromise;

      getDetail = () => {
        return new Promise(resolve => {
          detail = {
            deviceName: model,
            OSVersion: version,
            sdkVersion: info.getVersion(),
          };
          thisDevice.detail = detail;
          resolve(detail);
        });
      };
    } catch (err) {
      startupErrors.push(`tizen detail error: ${log.describeError(err)}`);
    }
  }

  const registerKeys = () => {
    var usedKeys = [
      'MediaPause',
      'MediaPlay',
      'MediaPlayPause',
      'MediaFastForward',
      'MediaRewind',
      'MediaStop',
      'MediaTrackNext',
      'MediaTrackPrevious',
    ];
    usedKeys.forEach(function (keyName) {
      try {
        tizen.tvinputdevice.registerKey(keyName);
      } catch (error) {
        console.log('Failed to register ' + keyName + ': ' + error);
      }
    });
  };

  const setPlayPosition = (action_id: unknown, bundle_message: { position?: number }) => {
    var resultCode = [{ result_code: 'SUCCESS' }];
    if (bundle_message.position !== undefined) {
      playerRef.aviaPlayer.seek(bundle_message.position);
    }
    onCompleteActionExecution(action_id, resultCode);
  };

  // Video will fastforward by 'offset' seconds or by 10 seconds if there is no offset
  const skipForward = (action_id: unknown, bundle_message?: { offset?: number }) => {
    const resultCode = [{ result_code: 'SUCCESS' }];
    const currentTime = playerRef.aviaPlayer.contentTime;
    const offset = bundle_message?.offset ? bundle_message.offset : 10;
    const newTime = parseFloat(currentTime) + offset;
    playerRef.aviaPlayer.seek(newTime);
    onCompleteActionExecution(action_id, resultCode);
  };

  // Video will rewind by 'offset' seconds or by 10 seconds if there is no offset
  const skipBackward = (action_id: unknown, bundle_message?: { offset?: number }) => {
    var resultCode = [{ result_code: 'SUCCESS' }];
    const currentTime = playerRef.aviaPlayer.contentTime;
    const offset = bundle_message?.offset ? bundle_message.offset : 10;
    const newTime = parseFloat(currentTime) - offset;
    playerRef.aviaPlayer.seek(Math.max(0, newTime));
    onCompleteActionExecution(action_id, resultCode);
  };

  const restartVideo = (action_id: unknown) => {
    const resultCode = [{ result_code: 'SUCCESS' }];
    playerRef.aviaPlayer.seek(0);
    onCompleteActionExecution(action_id, resultCode);
  };

  const onCompleteActionExecution = (action_id: unknown, resultCode: unknown) => {
    webapis.bixby.completeActionExecution(
      action_id,
      JSON.stringify(resultCode),
    );
  };

  if (webapis && webapis.bixby) {
    try {
      webapis.bixby.initialize();
      registerKeys();
      webapis.bixby.setActionExecutionListener(
        'tvMediaControl.SetPlayPosition',
        setPlayPosition,
      );
      webapis.bixby.setActionExecutionListener(
        'tvMediaControl.SkipForward',
        skipForward,
      );
      webapis.bixby.setActionExecutionListener(
        'tvMediaControl.SkipBackward',
        skipBackward,
      );
      webapis.bixby.setActionExecutionListener(
        'tvMediaControl.Restart',
        restartVideo,
      );
    } catch (err) {
      log.error(`Bixby error: ${log.describeErrorWithStack(err)}`);
    }
  }

  setPlayerRef = player => {
    playerRef = player;
  };

  const getIpAddressPromise = new Promise((resolve, reject) => {
    if (webapis && webapis.network) {
      const ipAddress = webapis.network.getIp();
      resolve(ipAddress);
    } else {
      reject();
    }
  });

  getIpAddress = () => getIpAddressPromise;

  getDeviceNetworkConnection = () =>
    new Promise((resolve, reject) => {
      if (webapis && webapis.network) {
        const networkStatus = webapis.network.isConnectedToGateway();
        resolve(networkStatus);
      } else {
        reject();
      }
    });

  setScreenSaver = on => {
    webapis.appcommon.setScreenSaver(
      on
        ? webapis.appcommon.AppCommonScreenSaverState.SCREEN_SAVER_ON
        : webapis.appcommon.AppCommonScreenSaverState.SCREEN_SAVER_OFF,
      function () {},
      function (error) {
        console.log('error: ', error);
      },
    );
  };

  hasSpeechSynthesisCapability = () =>
    tizen.systeminfo.getCapability(
      'http://tizen.org/feature/speech.synthesis',
    ) && !!window.speechSynthesis;

  accessibleTextDisplayEnabled = () =>
    captionsKey !== undefined && !!webapis.tvinfo.getMenuValue(captionsKey);

  accessibleAudioEnabled = async () => {
    if (!hasSpeechSynthesisCapability()) return false;
    return voiceKey !== undefined && !!webapis.tvinfo.getMenuValue(voiceKey);
  };

  subscribeToAccessibilityChanges = callback => {
    const captionChangeListener = () =>
      callback?.(accessibleTextDisplayEnabled());
    try {
      webapis.tvinfo.addCaptionChangeListener(captionsKey, captionChangeListener);
      webapis.tvinfo.addCaptionChangeListener(voiceKey, captionChangeListener);
    } catch (err) {
      log.error(`subscribeToAccessibilityChanges: ${log.describeError(err)}`);
    }
  };

  exitApp = () => {
    try {
      webapis.bixby.deinitialize();
    } catch (err) {
      // no-op
    }

    tizen.application.getCurrentApplication().exit();
  };

  const keyMap = getDefaultKeyMap();
  keyMap[appActions.select] = [keyCodes.enter, /* soft keyboard done: */ 65376];
  keyMap[appActions.back] = [
    keyCodes.esc,
    10009 /* tizen remote, but also support USB keyboard */,
  ];
  keyMap[appActions.cancel] = [65385 /* soft keyboard cancel */];

  // `inputdevice` is the older Tizen API name, kept as a compatibility fallback.
  const inputdevice =
    tizen.tvinputdevice ||
    (tizen as unknown as { inputdevice?: typeof tizen.tvinputdevice })
      .inputdevice;
  if (!inputdevice) {
    startupErrors.push('tizen.tvinputdevice not defined');
    startupErrors.push(JSON.stringify(dumpObject(tizen)));
  } else {
    // Remaining keys need to be explicitly registered to get access to them

    // var supportedKeys = inputdevice.getSupportedKeys();
    // var keyNames = {};
    // supportedKeys.forEach(function(keyDef) {
    //  keyNames[keyDef.name] = keyDef.code;
    // });
    // startupErrors.push("supportedKeys: {0}", JSON.stringify(keyNames));

    const keyNames = [
      '0',
      '1',
      '2',
      '3',
      '4',
      '5',
      '6',
      '7',
      '8',
      '9',
      'Exit',
      'MediaFastForward',
      'MediaPause',
      'MediaPlay',
      'MediaPlayPause',
      'MediaRewind',
      'MediaStop',
      'MediaTrackNext',
      'MediaTrackPrevious',
      'Search',
      'Extra',
    ];
    const tizenCodes: Record<string, number> = {};
    keyNames.forEach(keyName => {
      inputdevice.registerKey(keyName);
      const keyDef = inputdevice.getKey(keyName);
      if (keyDef) {
        tizenCodes[keyName] = keyDef.code;
      }
    });

    keyMap[appActions.exit] = tizenCodes.Exit;
    keyMap[appActions.play] = [tizenCodes.MediaPlay];
    keyMap[appActions.pause] = [tizenCodes.MediaPause];
    keyMap[appActions.playPause] = [tizenCodes.MediaPlayPause];
    keyMap[appActions.stop] = tizenCodes.MediaStop;
    keyMap[appActions.fastForward] = tizenCodes.MediaFastForward;
    keyMap[appActions.rewind] = tizenCodes.MediaRewind;
    // disable these, they interfere with FF/REW and holding.
    // keyMap[appActions.nextTrack] = tizenCodes["MediaTrackNext"];
    // keyMap[appActions.prevTrack] = tizenCodes["MediaTrackPrevious"];
    keyMap[appActions.search] = tizenCodes.Search;
    keyMap[appActions.extra] = tizenCodes.Extra; // extra key on Tizen remote, useful for qa actions
  }

  return keyMap;
}

function configureForVizio(): Record<string, number | number[]> {
  const { VIZIO } = window;

  requiresUserGestureForSpeech = false;

  supportsHLS = false; // use progressive streams until we work out HLS/SS playback issues
  supportsSmoothStreaming = false;
  useSystemCCSettings = true;
  supportsNativeCC = true;

  // Handle action for closed captions button on remote control
  useCCAppAction = false;

  // Vizio opera presto models seems to need the video to be loaded before initial seeks
  doInitialSeekOnlyAfterVideoStart = usesOpera;

  // disable to ensure scrollbars are always hidden in atuo-scale situations.
  useWindowScroll = false;

  preventInputFocus = true; // platform does not show the focus properly
  hasKeyboard = true; // Vizio TVs can have remotes with a key board underneath
  useVirtualKeyboard = true;

  const keyMap = getOperaTVKeyMap();
  keyMap[appActions.back] = [keyCodes.backspace, keyCodes.del];
  keyMap[appActions.exit] = [keyCodes.esc];
  keyMap[appActions.captions] = [460, 105];

  manufacturer = 'Vizio';
  let oem;
  let enableTts = false;

  // We always invoke TTS if we have the capability, it is only actually
  // audible depending on the TV's TTS settings.
  accessibleAudioEnabled = () =>
    Promise.resolve(enableTts && hasSpeechSynthesisCapability());

  subscribeToAccessibilityChanges = callback => {
    if (callback) {
      window.VIZIO.setClosedCaptionHandler(callback);
    }
  };

  exitApp = () => {
    VIZIO.exitApplication();
  };

  try {
    // Determine the device model and version.
    if (isVizioSmartCastDevice) {
      oem = 'SmartCast';
      platformVariant = `${platform}-${oem}`;
      supportsSmoothStreaming = true;
      useDRM = true;
      isSlowDevice = true;

      const getDeviceIdPromise = new Promise<string>(resolve => {
        const { VIZIO } = window;
        VIZIO.getDeviceId(deviceId => {
          if (deviceId) {
            resolve(deviceId);
          } else if (isLocalStorageSupported) {
            defaultGetDeviceId.then(id => resolve(id));
          }
        });
      });

      getDeviceId = () => getDeviceIdPromise;

      startTTS = text => {
        if (window.VIZIO.Chromevox) {
          window.VIZIO.Chromevox.play(text);
        }
      };

      stopTTS = () => {
        if (window.VIZIO.Chromevox) {
          window.VIZIO.Chromevox.cancel();
        }
      };

      getDetail = () => {
        const { VIZIO } = window;
        const { deviceInfo } = VIZIO;
        if (deviceInfo) {
          return new Promise(resolve => {
            detail = {
              deviceName: deviceInfo.model,
              OSVersion: deviceInfo.SYSTEM_INFO?.VERSION,
              sdkVersion: deviceInfo.API_VERSION,
              modelSeries: deviceInfo.modelSeries,
            };
            thisDevice.detail = detail;
            resolve(detail);
          });
        }
        return Promise.resolve(detail);
      };

      const advertiserIDPromise = new Promise<void>(resolve => {
        // IFA value to be used as Resettable Device Id
        window.VIZIO.setAdvertiserIDListener(AdvertiserID => {
          if (AdvertiserID) {
            rdid = AdvertiserID.IFA;
            isLAT = !!AdvertiserID.LMT;
            if (advertiserIdListener) {
              advertiserIdListener(rdid, isLAT);
            }
            resolve();
          }
        });
      });

      getRDID = () => advertiserIDPromise.then(() => rdid);
      getLAT = () => advertiserIDPromise.then(() => isLAT);
    } else if (typeof window.iMTK_JSP === 'function') {
      isSlowDevice = true;
      const oimtk = new iMTK_JSP();
      const oconfig = oimtk.imtkconfig;
      model = oconfig.getConfig('modelName');
      version = oconfig.getConfig('versionNumber');
      oem = 'MTK';
      enableTts = !usesOpera; // we assume only the non-Presto versions have TTS support

      // Track if this is the old MTK model, there are some platform issues around
      // HTTPS usage and Adobe heartbeat libraries.
      isVizioOldMtk = usesOpera;
    } else {
      // Assume this is a sigma device, extra the details from the user agent header, e.g.
      // userAgent = "Opera/9.80 (Linux armv71) Presto/2.12.407 Version/12.51 , M55-C2-UHD/1.3.15B-UHD (Vizio, M55-C2, Wired)"
      // userAgent = "Mozilla/5.0 (Linux armv7l) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/53.0.2785.143 Safari/537.36 OPR/40.0.2207.0 OMI/4.9.0.73.Clio.10 , _TV_D43-E2-UHD/17D-0.8.8.3-UHD (Vizio, D43-E2, Wireless) Model/SigmaDesigns-SX7";
      let vizioGroupStart = userAgent.lastIndexOf('(Vizio');
      if (vizioGroupStart < 0) vizioGroupStart = userAgent.length;
      const modelSegmentSlash = userAgent.lastIndexOf('/', vizioGroupStart);
      if (modelSegmentSlash >= 0) {
        const segmentEnd = userAgent.indexOf(' ', modelSegmentSlash);
        const segmentStart = userAgent.lastIndexOf(' ', modelSegmentSlash) + 1;
        if (segmentStart > 0 && segmentEnd > 0) {
          model = userAgent.slice(segmentStart, modelSegmentSlash);
          version = userAgent.slice(modelSegmentSlash + 1, segmentEnd);
        }
      }

      // On the 2017 MTK model, we have an Opera/Presto user agent, but without the MTK api above:
      // Opera/9.80 (Linux armv7I) Presto/2.12.362 Version/12.11
      const assumeSigma = model && model !== unknownModel;
      if (assumeSigma) oem = 'Sigma';

      if (usesOpera) {
        if (assumeSigma) {
          // The Presto Sigma models support Smooth Streaming better. That should also take care of the seek issues.
          supportsSmoothStreaming = true;
          useDRM = true;
        } else {
          // Assume we are on the newer MTK model.
          oem = 'MTK';
          enableTts = true;
        }
      } else {
        // Non-presto should support SS+playready
        supportsSmoothStreaming = !!(assumeSigma);
        useDRM = true;
        // useDRM = false; // for 2017 testing, our TV doesn't support DRM for some reason
        useEME = false; // we don't yet require EME for 2017 Vizio
        // supportsHLS = true; // SS playback gives better results over HLS

        // Both ARIA support and browser focus causes bugs, the latter messes up scrolling
        // useAriaAccessibility = false; // ARIA accessibility standard supported by 2017 models and up
        // useTtsFocus = true; // ARIA support on Vizio is buggy, we use direct TTS calls instead

        enableTts = !!(assumeSigma);
      }
    }
    detail = `${model}/${version}/${oem}`;
    supportedFeatures.supportsHDR10 = VIZIO.getDeviceHDRCapabilities(
      capabilities => capabilities.includes('HDR10'),
    );
    supportedFeatures.supportsDolbyVision = VIZIO.getDeviceHDRCapabilities(
      capabilities => capabilities.includes('Dolby Vision'),
    );
  } catch (err) {
    startupErrors.push(`vizio detail error: ${err || 'empty err'}`);
  }

  return keyMap;
}

function configureForVega(): Record<string, number | number[]> {
  manufacturer = 'Vega';

  requiresUserGestureForSpeech = false;

  // Fire TV Chromium WebView — native HLS, EME/Widevine available
  supportsHLS = true;
  supportsSmoothStreaming = false;
  supportsWidevineDASH = true;
  useDRM = true;
  useEME = true;

  isFullyPerformant = true;
  useWindowScroll = false;
  useCssTransitions = true;
  useSystemCCSettings = true;
  supportsAutoSuspendResumePlayback = true;

  setPlayerRef = player => {
    playerRef = player;
  };

  exitApp = () => {
    window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'exit' }));
  };


  hasSpeechSynthesisCapability = () => !!window.ReactNativeWebView;

  hasVegaBridge = () => !!window.ReactNativeWebView;

  accessibleAudioEnabled = () =>
    Promise.resolve(
      !!(window as unknown as Record<string, unknown>)
        .__VEGA_SCREEN_READER_ENABLED__,
    );

  startTTS = text => {
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({ type: 'tts', data: text }),
    );
  };

  stopTTS = () => {
    window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'tts:stop' }));
  };

  const VEGA_DEVICE_TIMEOUT_MS = 5000;
  interface VegaDeviceData {
    deviceId?: string | null;
    osVersion?: string | null;
    osName?: string | null;
    privacy?: {
      limitAdTracking?: boolean | null;
      advertisingId?: string | null;
    };
  }
  let resolveVegaDeviceData: (data: VegaDeviceData) => void = () => {};
  const vegaDeviceDataPromise = new Promise<VegaDeviceData>(resolve => {
    resolveVegaDeviceData = resolve;
  });
  const vegaValueWithFallback = <T>(
    getValue: (data: VegaDeviceData) => T | Promise<T>,
    getFallback: () => T | Promise<T>,
  ): Promise<T> =>
    Promise.race([
      vegaDeviceDataPromise.then(getValue),
      new Promise<T>(resolve => {
        setTimeout(() => resolve(getFallback()), VEGA_DEVICE_TIMEOUT_MS);
      }),
    ]);

  getDeviceId = () =>
    vegaValueWithFallback(
      data => data.deviceId || defaultGetDeviceId,
      () => defaultGetDeviceId,
    );
  getRDID = () =>
    vegaValueWithFallback(
      data => data.privacy?.advertisingId || rdid,
      () => rdid,
    );
  getLAT = () =>
    vegaValueWithFallback(
      data =>
        typeof data.privacy?.limitAdTracking === 'boolean'
          ? data.privacy.limitAdTracking
          : isLAT,
      () => isLAT,
    );

  const applyVegaDeviceData = (data: VegaDeviceData) => {
    if (data.osVersion) {
      version = data.osVersion;
      detail = {
        deviceName: data.osName || 'Vega',
        OSVersion: data.osVersion,
        sdkVersion: data.osVersion,
      };
    }
    if (data.privacy) {
      if (data.privacy.advertisingId) {
        rdid = data.privacy.advertisingId;
      }
      if (typeof data.privacy.limitAdTracking === 'boolean') {
        isLAT = data.privacy.limitAdTracking;
      }
      if (advertiserIdListener) {
        advertiserIdListener(rdid, isLAT);
      }
    }
    resolveVegaDeviceData(data);
  };

  const accessibilityChangeListeners: Array<(enabled: boolean) => void> = [];

  const windowState = window as unknown as {
    __vegaAccessibilityMessageHandler?: (e: MessageEvent) => void;
  };
  if (windowState.__vegaAccessibilityMessageHandler) {
    window.removeEventListener(
      'message',
      windowState.__vegaAccessibilityMessageHandler,
    );
  }
  const vegaAccessibilityMessageHandler = (e: MessageEvent) => {
    try {
      const msg = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
      if (msg?.type === 'vega:device' && msg.data) {
        applyVegaDeviceData(msg.data as VegaDeviceData);
      } else if (msg?.type === 'vega:accessibility') {
        const enabled = !!msg.enabled;
        (window as unknown as Record<string, unknown>).__VEGA_SCREEN_READER_ENABLED__ =
          enabled;
        if (enabled) {
          document.dispatchEvent(new Event('ttsInitEvent'));
        }
        accessibilityChangeListeners.forEach(listener => listener(enabled));
      }
    } catch {
      // Not a JSON bridge message — ignore
    }
  };
  windowState.__vegaAccessibilityMessageHandler = vegaAccessibilityMessageHandler;
  window.addEventListener('message', vegaAccessibilityMessageHandler);

  subscribeToAccessibilityChanges = callback => {
    if (!callback) return;
    accessibilityChangeListeners.push(callback);
  };

  // Key codes sourced from video-vega/src/components/WebViewScreen.tsx injectedJavaScript
  const keyMap = getDefaultKeyMap();
  keyMap[appActions.play] = 415;        // MediaPlay
  keyMap[appActions.pause] = 19;        // MediaPause
  keyMap[appActions.playPause] = 179;   // MediaPlayPause
  keyMap[appActions.stop] = 413;        // MediaStop
  keyMap[appActions.fastForward] = 417; // MediaFastForward
  keyMap[appActions.rewind] = 412;      // MediaRewind
  keyMap[appActions.nextTrack] = 176;   // MediaNextTrack
  keyMap[appActions.prevTrack] = 177;   // MediaPreviousTrack

  return keyMap;
}

function configureForTcl(): Record<string, number | number[]> {
  isSlowDevice = true;
  supportsHLS = true;
  supportsSmoothStreaming = false; // plays with distortions at low bandwidths

  supportsInitialVideoSeek = false; // we have to wait to play is underway before seeks can occur
  seekIsReliable = false;

  // disable to ensure scrollbars are always hidden in auto-scale situations.
  useWindowScroll = false;

  // use our in-app keyboard after all, since performance of password entry is pretty bad
  preventInputFocus = true;
  // keyboardUsesInputField = true; // we need to show the input field while the keyboard is displaed.
  // showVirtualKeyboard = showImplicitKeyboard;

  const keyMap = getOperaTVKeyMap();

  manufacturer = 'TCL';
  model = undefined;
  version = undefined;
  if (userAgent.indexOf('Tbrowser/') >= 0) {
    model = 'MT5655';
    const userAgentParts = userAgent.split(';');
    for (let i = 0; i < userAgentParts.length; i += 1) {
      const part = userAgentParts[i];
      if (!version) version = getPrefixedValue(part, 'SW-Version/');
    }
  } else {
    // Determine the device other TCL platforms' model and version.
    // The first part after the single comma has the model and version.
    const userAgentParts = userAgent.split(' ');
    for (let i = 0; i < userAgentParts.length; i += 1) {
      const part = userAgentParts[i];
      // @ts-ignore
      if (!model) model = getPrefixedValue(part, 'Model/');
      // @ts-ignore
      if (!version) version = getPrefixedValue(part, 'Version/');

      if (!version) {
        // The MS6488 model uses the SRAF browser, which uses the (Opera?) Blink engine.
        // @ts-ignore
        version = getPrefixedValue(part, 'SRAF/');
        if (version) {
          model = 'MS6488';
        }
      }

      if (!version) {
        // The older user agent format MT5655 model uses the TBrowser, which uses Webkit.
        // @ts-ignore
        version = getPrefixedValue(part, 'Tbrowser/');
        if (version) {
          version = version.replace(/,$/, '');
          if (i + 1 < userAgentParts.length) {
            const nextPart = userAgentParts[i + 1];
            // @ts-ignore
            model = getPrefixedValue(nextPart, 'Digital-TV/');
          }
          if (!model) {
            model = 'MT5655';
          }
        }
      }
    }
  }

  if (model && version) {
    detail = `${model}/v${version}`;
  }

  return keyMap;
}

function configureForTiVo(): Record<string, number | number[]> {
  isSlowDevice = true;
  supportsHLS = false;
  supportsSmoothStreaming = true;
  useDRM = true;

  // disable to ensure scrollbars are always hidden in auto-scale situations.
  useWindowScroll = false;

  // use our in-app keyboard after all, since performance of password entry is pretty bad
  preventInputFocus = true;
  // keyboardUsesInputField = true; // we need to show the input field while the keyboard is displaced.
  // showVirtualKeyboard = showImplicitKeyboard;

  const keyMap = getOperaTVKeyMap();

  const tivoKeyboardEvent = window.KeyboardEvent;

  if (tivoKeyboardEvent) {
    // @ts-ignore - TiVo-specific KeyboardEvent constants
    keyMap[appActions.playPause] = [
      // @ts-ignore - TiVo-specific KeyboardEvent constants
      tivoKeyboardEvent.DOM_VK_PLAY_PAUSE,
      // @ts-ignore - TiVo-specific KeyboardEvent constants
      tivoKeyboardEvent.DOM_VK_PLAY,
      // @ts-ignore - TiVo-specific KeyboardEvent constants
      tivoKeyboardEvent.DOM_VK_PAUSE,
    ];
    // @ts-ignore - TiVo-specific KeyboardEvent constants
    keyMap[appActions.fastForward] = tivoKeyboardEvent.DOM_VK_FAST_FWD;
    // @ts-ignore - TiVo-specific KeyboardEvent constants
    keyMap[appActions.rewind] = tivoKeyboardEvent.DOM_VK_REWIND;
  }

  manufacturer = 'TiVo';
  // @ts-ignore
  model = undefined;
  // @ts-ignore
  version = undefined;

  const modelEndIndex = userAgent.lastIndexOf(',') - 1;
  if (modelEndIndex > -1) {
    const modelBeginIndex = userAgent.lastIndexOf(' ', modelEndIndex) + 1;
    if (modelBeginIndex > -1)
      model = userAgent.slice(modelBeginIndex, modelEndIndex);
  }

  const versionBeginIndex = userAgent.lastIndexOf('/') + 1;
  if (versionBeginIndex > -1) {
    const versionEndIndex = userAgent.indexOf(' ', versionBeginIndex) - 1;
    if (versionEndIndex > -1)
      version = userAgent.slice(versionBeginIndex, versionEndIndex);
  }

  return keyMap;
}

function configureForSony(): Record<string, number | number[]> {
  isSlowDevice = usesOpera;
  supportsHLS = true;
  supportsSmoothStreaming = true;
  useDRM = true;

  // disable to ensure scrollbars are always hidden in auto-scale situations.
  useWindowScroll = false;

  // We need to use cookies instead instead of local storage on Opera. Usually. Some BLu-Ray devices work with cookies
  // but the 2017 Blu-Ray does not seem reliable. So we write to both cookies and local storage for reliability.
  useBothLocalAndCookieStorage = true;

  // use our in-app keyboard after all, since performance of password entry is pretty bad
  preventInputFocus = true;
  // keyboardUsesInputField = true; // we need to show the input field while the keyboard is displaed.
  // showVirtualKeyboard = showImplicitKeyboard;

  const keyMap = getOperaTVKeyMap();
  keyMap[appActions.fastForward] = [417, 465]; // blue ray has slightly different code it seems.

  // Determine the device model and version.
  manufacturer = 'Sony';
  const userAgentParts = userAgent.split(' ');
  model = undefined;
  version = undefined;
  for (let i = 0; i < userAgentParts.length; i += 1) {
    const part = userAgentParts[i];
    if (part.match(/SonyCEBrowser/) && i + 2 < userAgentParts.length) {
      const modelPart = userAgentParts[i + 1];
      model = getPrefixedValue(modelPart, '(') as string;
      model = model.replace(/[:;]$/, '');

      version = userAgentParts[i + 2];
      version = version.replace(/[:;]$/, '');
      break;
    } else if (!version) {
      version = getPrefixedValue(part, 'OMI/');
    } else if (!model) {
      model = getPrefixedValue(part, 'Model/') as string;
    }
  }
  detail = `${model}/${version}`;

  isSonyBlueRay = !!(model && model.indexOf('BDP') >= 0);

  return keyMap;
}

function configureForSagem(): Record<string, number | number[]> {
  supportsHLS = true;
  supportsSmoothStreaming = true;
  useDRM = true;

  // use our in-app keyboard after all, since performance of password entry is pretty bad
  preventInputFocus = true;

  const keyMap = getOperaTVKeyMap();

  manufacturer = 'Sagem';
  const userAgentParts = userAgent.split(' ');
  const modelParts = (userAgentParts[1] && userAgentParts[1].split('/')) || [];
  [model, version] = modelParts;
  detail = `${model}/${version}`;

  return keyMap;
}

function configureForNetgem(): Record<string, number | number[]> {
  supportsHLS = true;
  supportsSmoothStreaming = true;
  useDRM = true;

  // use our in-app keyboard after all, since performance of password entry is pretty bad
  preventInputFocus = true;

  const keyMap = getOperaTVKeyMap();

  manufacturer = 'Netgem';
  const userAgentParts = userAgent.split(' ');
  [, version, , model] = userAgentParts;
  if (model) model = model.replace(',', '');
  detail = `${model}/${version}`;

  return keyMap;
}

function configureForLgWebOs(): Record<string, number | number[]> {
  requiresUserGestureForSpeech = false;

  isFullyPerformant = true;
  useCssAnimation = false; // supposed to work, but doesn't seem to
  useSvgAnimation = true; // this does work

  usePPID = true;

  supportsHLS = true;
  supportsSmoothStreaming = true;
  useDRM = true;
  useSystemCCSettings = true;
  supportsNativeCC = true;

  // use scrollTop changes instead of window.scrollTo() on LG
  useWindowScroll = false;
  useScrollTop = true;

  // need to force our own virtual keyboard, but prevent accidental input focus clicks from showing
  // the LG keyboard. (Our own soft keyboard is in fact more usable than the LG native one,
  // due to accent chars, wrap around, etc.)
  preventInputFocus = true;
  // keyboardUsesInputField = true;
  // showVirtualKeyboard = showImplicitKeyboard;

  useAriaAccessibility = true;

  const keyMap = getLGKeyMap();

  // Determine the device details.
  const { webOS } = window;
  invariant(webOS && webOS.platform, 'webOS not loaded');

  manufacturer = 'LG';

  hasSpeechSynthesisCapability = () => true;

  accessibleAudioEnabled = () =>
    new Promise<boolean>(resolve => {
      webOS.service.request('luna://com.webos.settingsservice', {
        method: 'getSystemSettings',
        parameters: {
          category: 'option',
          keys: ['audioGuidance'],
        },
        onSuccess: (response: { settings: { audioGuidance: string } }) => {
          try {
            const { audioGuidance } = response.settings;
            resolve(audioGuidance === 'on');
          } catch {
            resolve(false);
          }
        },
        onFailure: () => {
          resolve(false);
        },
      });
    });

  startTTS = string => {
    webOS.service.request('luna://com.webos.service.tts', {
      method: 'speak',
      parameters: { text: string, clear: true },
      onSuccess: () => {
        return;
      },
      onFailure: () => {
        return;
      },
    });
  };

  // The stop TTS method (https://www.webosose.org/docs/reference/ls2-api/com-webos-service-tts/#stop) doesn't work
  stopTTS = () => {
    webOS.service.request('luna://com.webos.service.tts', {
      method: 'speak',
      parameters: { text: '', clear: true },
      onSuccess: () => {
        return;
      },
      onFailure: () => {
        return;
      },
    });
  };

  // Get caption information
  const LGClosedCaptionPromise = (callback?: (enabled: boolean) => void) =>
    new Promise<string>((resolve, reject) => {
      webOS.service.request('luna://com.webos.settingsservice', {
        method: 'getSystemSettings',
        parameters: {
          category: 'caption',
          keys: ['captionEnable'],
          subscribe: true,
        },
        onSuccess: (result: { settings: { captionEnable: string } }) => {
          const { captionEnable } = result.settings;
          callback?.(captionEnable === 'on');
          resolve(captionEnable);
        },
        onFailure: () => {
          reject();
        },
      });
    });

  subscribeToAccessibilityChanges = callback =>
    LGClosedCaptionPromise(callback);

  let mvpdDeviceIdPromise: Promise<string> | null = null;

  getDeviceId = () => {
    if (!mvpdDeviceIdPromise) {
      mvpdDeviceIdPromise = (async () => {
        const storage = config.useDb8Storage ? db8Storage : null;
        const storedId = storage
          ? await storage.getItem(MVPD_DEVICE_ID_KEY)
          : safeLocalStorage.getItem(MVPD_DEVICE_ID_KEY);
        if (storedId) {
          return storedId;
        }

        if (storage) {
          await storage.setItem(MVPD_DEVICE_ID_KEY, deviceId);
        } else {
          safeLocalStorage.setItem(MVPD_DEVICE_ID_KEY, deviceId);
        }
        return deviceId;
      })().catch(err => {
        mvpdDeviceIdPromise = null;
        throw err;
      });
    }

    return mvpdDeviceIdPromise;
  };
  getRDID = () => Promise.resolve(deviceId);
  getLAT = () => Promise.resolve(false);
  getDeviceNetworkConnection = () =>
    new Promise((resolve, reject) => {
      webOS.service.request('luna://com.palm.connectionmanager', {
        method: 'getStatus',
        onSuccess: function (result: Record<string, { state?: string }>) {
          const networkType = findKey(result, ['state', 'connected']);
          resolve(networkType ?? false);
        },
        onFailure: function () {
          reject();
        },
      });
    });

  const getIpAddressPromise = new Promise((resolve, reject) => {
    webOS.service.request('luna://com.webos.service.connectionmanager', {
      method: 'getStatus',
      onSuccess: function (result: Record<string, { ipAddress: string }>) {
        const { wifi, wired } = result;
        const ip = wired.ipAddress || wifi.ipAddress;
        resolve(ip);
      },
      onFailure: function () {
        reject();
      },
    });
  });

  getIpAddress = () => getIpAddressPromise;

  // Computing the detail is asynchronous on LG WebOS.
  getDetail = () =>
    new Promise(resolve => {
      webOS.deviceInfo(info => {
        const device = info as { version: string; modelName: string; uhd: boolean; sdkVersion: string; screenWidth: number; screenHeight: number };
        const { version, modelName } = device;
        thisDevice.model = modelName;
        thisDevice.version = version;
        thisDevice.supportedFeatures.supports4K = device.uhd;

        detail = {
          deviceName: modelName,
          OSVersion: version,
          sdkVersion: device.sdkVersion,
          screenWidth: device.screenWidth,
          screenHeight: device.screenHeight,
        };
        thisDevice.detail = detail;
        resolve(detail);
      });
    });

  return keyMap;
}

function configureForLgNetCast(): Record<string, number | number[]> {
  useCssAnimation = false; // doesn't seem to work, even though the docs say they do
  useCssTransitions = false; // works, but not giving good results in this app

  supportsHLS = true;
  supportsSmoothStreaming = true;
  useDRM = true;

  usesCORS = false;

  // use scrollTop changes instead of window.scrollTo() on LG
  useWindowScroll = false;
  useScrollTop = true;
  areScrollBarsVisible = true; // we need to account for this

  supportsInitialVideoSeek = false; // initial seek sometimes works, sometimes does not.
  pauseForInitialVideoSeek = true; // try to avoid any initial frame 0 from appearing
  allowPlaybackWithoutDuration = true;
  // seekIsReliable = false; // sometimes reliable, but only after the duration is known; sometimes seeks fail to occur

  // we work with our own virtual keyboard, and prevent accidental input focus clicks from having any effect
  preventInputFocus = true;
  // keyboardUsesInputField = true;
  // showVirtualKeyboard = showImplicitKeyboard;

  exitApp = () => {
    window.NetCastExit();
  };

  const keyMap = getLGKeyMap();

  manufacturer = 'LG';

  try {
    // Determine the device details.
    const device = document.getElementById('lg-device') as unknown as {
      modelName: string;
      version: string;
      swVersion: string;
      hwVersion: string;
      SDKVersion: string;
      osdResolution: string;
    };
    model = device.modelName;
    const platformVersion = device.version;
    const { swVersion } = device;
    const { hwVersion } = device;
    const sdkVersion = device.SDKVersion;
    version = swVersion;
    let resolution = device.osdResolution;
    const resolutionsEnum = [
      '640x480',
      '720x576',
      '1280x720',
      '1920x1080',
      '1366x768',
    ];
    const resolutionSize = resolutionsEnum[parseInt(resolution, 10)];
    if (resolutionSize) {
      resolution = resolutionSize;
    }
    detail = `${model}/v${platformVersion}/sw=${swVersion}/hw=${hwVersion}/sdk=${sdkVersion}/screen=${resolution}`;
  } catch (err) {
    startupErrors.push(`LG detail error: ${log.describeError(err)}`);
  }

  return keyMap;
}

function configureForPS3(): Record<string, number | number[]> {
  isSlowDevice = true;
  isMemoryConstrained = true; // memory is tight on the PS3

  hasNumericKeys = false;

  supportsHLS = false;
  supportsSmoothStreaming = true;
  useDRM = false;

  supportsAutoSuspendResumePlayback = true;

  useWindowScroll = false;

  supportsSvg = false;

  // we need to use cookies instead instead of local storage
  isLocalStorageSupported = false;

  manufacturer = 'Sony';

  // Read the device detail after the WebMAF layer has initialized
  // Just by delay reading device properties through a function allows
  // the WebMAF layer to be initialized properly
  getDetail = () =>
    new Promise(resolve => {
      thisDevice.model = model = 'PS3';
      ({ version } = WM_devSettings);
      thisDevice.version = version;
      detail = `${model}|${version}`;
      thisDevice.detail = detail;
      resolve({
        deviceName: model,
        OSVersion: 'UNKNOWN',
        sdkVersion: version,
        screenWidth: 'UNKNOWN',
        screenHeight: 'UNKNOWN',
      });
    });

  const keyMap = getPlaystationKeyMap();
  return keyMap;
}

function configureForPS4(): Record<string, number | number[]> {
  isFullyPerformant = true;
  supportsHLS = false;
  supportsSmoothStreaming = true;
  supportsPlayreadyDASH = true;
  useDRM = false;
  useWindowScroll = false;

  supportsAutoSuspendResumePlayback = true;

  supportedFeatures.supportsHDR10 = true;
  supportedFeatures.supportsDolbyDigital = true;

  // The loading spinner CSS animations are causing display issues (blinking images)
  // for content overlayed over the background video player
  useCssAnimation = false;

  hasNumericKeys = false;

  keyboardUsesInputField = true;
  showVirtualKeyboard = showImplicitKeyboard;

  const root = document.documentElement;
  const screenW = root.clientWidth;
  const screenH = root.clientHeight;
  const keyboardW = 790;
  const keyboardH = 530;
  // @ts-ignore - PlayStation external.user API
  window.external.user(
    JSON.stringify({
      command: 'setOskPosition',
      x: (screenW - keyboardW) / 2,
      y: (screenH - keyboardH) / 2,
    }),
  );

  manufacturer = 'Sony';

  const getDeviceIdPromise = new Promise<string>((resolve, reject) => {
    const prevAccessFunc = window.accessfunction;
    window.accessfunction = ((json: string, ...rest: unknown[]) => {
      console.log(`accessfunction json response: ${json}`);
      const result = JSON.parse(json);
      if (result.command === 'hwid') {
        window.accessfunction = prevAccessFunc;
        if (result.status === 'ok') resolve(result.hwid);
        else if (result.status === 'fail') reject('');
      } else if (prevAccessFunc) {
        prevAccessFunc(json, ...rest);
      }
    }) as CallableFunction;

    // @ts-ignore - PlayStation external.user API
    window.external.user(
      JSON.stringify({
        command: 'hwid',
      }),
    );
  });

  getDeviceId = () => getDeviceIdPromise;

  // Read the device detail after the WebMAF layer has initialized
  // Just by delay reading device properties through a function allows
  // the WebMAF layer to be initialized properly
  getDetail = () => {
    return new Promise(resolve => {
      model = 'PS4';
      thisDevice.model = model;

      const { version, deviceName } = window.WM_devSettings;

      thisDevice.version = version;

      detail = { deviceName, version };
      thisDevice.detail = detail;
      resolve({
        deviceName: deviceName,
        OSVersion: 'UNKNOWN',
        sdkVersion: version,
        screenWidth: 'UNKNOWN',
        screenHeight: 'UNKNOWN',
      });
    });
  };

  const keyMap = getPlaystationKeyMap();
  return keyMap;
}

function configureForUniversalWindows(): Record<string, number | number[]> {
  isFullyPerformant = true;
  supportsHLS = false;
  supportsSmoothStreaming = true;
  supportsPlayreadyDASH = true;
  useDRM = true;
  useEME = true;
  useWindowScroll = true;
  supportsImageMasks = false;
  hasNumericKeys = false;
  isUniversalWindows = true;
  supportsTouch = true;

  keyboardUsesInputField = true;
  showVirtualKeyboard = showImplicitKeyboard;

  const analyticsInfo = Windows.System.Profile.AnalyticsInfo;
  const { versionInfo } = analyticsInfo;

  manufacturer = 'Windows';
  model = versionInfo.deviceFamily;
  version = versionInfo.deviceFamilyVersion;

  const buildVersion = ((version as unknown as number) & 0xffff0000) >> 16;

  const releases = {
    VERSION1507: 10240, // Initial Release
    VERSION1511: 10586, // November Update
    VERSION1607: 14393, // Anniversary Update
  };
  windowsBuildVersionInfo = {
    RELEASES: releases,
    buildVersion,
  };

  detail = `${model}/${version}`;

  const keyMap = getUniversalWindowsKeyMap();
  return keyMap;
}

function configureForXboxOne(): Record<string, number | number[]> {
  const keyMap = configureForUniversalWindows();
  supportsHLS = false;
  useCssTransitions = false;
  useDRM = true;
  supportsTouch = false;

  supportedFeatures.supportsDolbyVision = true;
  supportedFeatures.supportsHDR10 = true;
  supportedFeatures.supportsDolbyAtmos = true;
  supportedFeatures.supportsDolbyDigital = true;

  supportsSvg = false;

  return keyMap;
}

function configureForAndroidTV(): Record<string, number | number[]> {
  isFullyPerformant = true; // assume yes, deal with lower end devices on a case by case basis
  useWindowScroll = false;

  useCssTransitions = false;
  supportsSmoothStreaming = false;
  supportsWidevineDASH = true;
  useDRM = true;

  supportsAutoSuspendResumePlayback = true;

  accessibleTextDisplayEnabled = () =>
    !!(window.androidTVApp &&
    window.androidTVApp.isAndroidTextMagnificationEnabled &&
    window.androidTVApp.isAndroidTextMagnificationEnabled());

  accessibleAudioEnabled = () =>
    Promise.resolve(
      !!(window.androidTVApp &&
      window.androidTVApp.isAndroidTalkBackEnabled &&
      window.androidTVApp.isAndroidTalkBackEnabled()),
    );

  // A bit hacky! Assumes android app bridge will be named "androidTVApp"
  if (window.androidTVApp) {
    const detailString = window?.androidTVApp?.getAndroidDetails?.() ?? '';
    const details = detailString.split(',');

    model = `${details[0]} ${details[1]}`;
    [, , version] = details;
    detail = details.join(' ');
  } else {
    const detailSubstring = userAgent.substring(
      userAgent.indexOf('(') + 1,
      userAgent.indexOf(')'),
    );
    const detailSplit = detailSubstring.split(';');

    [, version, model] = detailSplit;
    detail = model;
  }

  const keyMap = getAndroidTVKeyMap();
  return keyMap;
}

function configureForFireTV(): Record<string, number | number[]> {
  // use androidTV configurations
  const keyMap = configureForAndroidTV();

  return keyMap;
}

/**
 * Many platforms show an implicit keyboard when the input field is focused.
 * @param inputElementId
 * @return {Promise} that returns the text when the user commits the value, or undefined if the keyboard was cancelled.
 */
function showImplicitKeyboard(inputElementId: string, getFinalInputText?: () => Promise<string>): Promise<string | undefined> {
  return new Promise((resolve, reject) => {
    let inputField: HTMLInputElement;
    let isClosed = false;
    let isResolved = false;

    try {
      inputField = document.getElementById(inputElementId) as HTMLInputElement;
      inputField.blur();
      isShowingVirtualKeyboard = true;
      if (PLATFORM_PS4 === platform) {
        // Input does not receive events while keyboard is shown for PS4
        inputField.select();

        if (getFinalInputText) {
          // We obtain the final input text from the WebMAF oskClose command.
          getFinalInputText().then(result => {
            result = inputField.value; // Value from response.inputText does not seem to handle asian characters
            handleKeyboardCleanup();
            resolve(result);
          });
        }
      } else {
        if (isUniversalWindows) {
          inputField.readOnly = false;

          const inputPane = Windows.UI.ViewManagement.InputPane.getForCurrentView();
          if (inputPane) {
            inputPane.tryShow();

            // clean up for dismissing input pane
            inputPane.onhiding = () => {
              if (!isResolved) {
                handleKeyboardCleanup();
                // ensure UWP keyboard display won't be triggered
                inputField.readOnly = true;
                // @ts-ignore
                resolve();
              }
            };
          }
        }
        inputField.addEventListener('keydown', handleKeystroke as EventListener);
        inputField.focus();
      }
    } catch (err) {
      log.error(`inputField error: ${log.describeErrorWithStack(err)}`);
      reject(err);
    }

    function handleKeystroke(event: KeyboardEvent): boolean | undefined {
      if (isClosed) return;

      const { keyCode } = event;
      try {
        const action = getKeyCodeAction(keyCode);
        // log.debug("handleKeystroke: {0} gives {1}", keyCode, action);
        const isDone = action === appActions.select;
        let isCancel = action === appActions.back || action === appActions.exit;
        if (action === appActions.back && inputField.value) {
          // If we have input, let the backspace keystroke be used to delete the previous character.
          isCancel = false;
        }
        if (isCancel || isDone) {
          // Call UWP hide instead and let it deal with the cleanup
          if (isUniversalWindows) {
            const inputPane = Windows.UI.ViewManagement.InputPane.getForCurrentView();
            if (inputPane) inputPane.tryHide();
          } else {
            handleKeyboardCleanup();
          }
          const finalResult = isDone ? inputField.value : undefined;
          resolve(finalResult);
          return false;
        }
      } catch (err) {
        log.error(`handleKeystroke: ${log.describeErrorWithStack(err)}`);
      }
      return true;
    }

    function handleKeyboardCleanup(): void {
      isResolved = true;
      isClosed = true;
      inputField.blur();
      inputField.removeEventListener('keydown', handleKeystroke as EventListener);
      isShowingVirtualKeyboard = false;
    }
  });
}

function getKeyCodeAction(keyCode: number): string | undefined {
  let result: string | undefined;
  for (let i = 0; i < Object.keys(actionKeyMap).length; i += 1) {
    const action = Object.keys(actionKeyMap)[i];
    const keyCodeArr = actionKeyMap[action];
    if (
      (typeof keyCodeArr === 'number' && keyCodeArr === keyCode) ||
      (Array.isArray(keyCodeArr) && keyCodeArr.indexOf(keyCode) >= 0)
    ) {
      result = action;
      break;
    }
  }
  // log.debug("key code {0} maps to {1}", keyCode, result);
  return result;
}

function nextAriaHiddenElement(): HTMLElement | null {
  // We alternate between two current aria bindings to ensure that things change.
  if (!firstHiddenAriaElement) {
    firstHiddenAriaElement = document.getElementById('aria-hidden-1');
    secondHiddenAriaElement = document.getElementById('aria-hidden-2');
  }

  const ariaHiddenElement = usingAriaFocus1
    ? secondHiddenAriaElement
    : firstHiddenAriaElement;
  usingAriaFocus1 = !usingAriaFocus1;
  return ariaHiddenElement;
}

export const isUnknownOrNull = (param: string | number | null): boolean => {
  return param === null || param.toString().toLowerCase() === 'unknown';
};
